import type { ActionContext, Module } from 'vuex';
import { getCrmRequests } from '../api/api';
import { commitEntity } from './helpers';
import type {
    Block,
    BlockWritePayload,
    FieldCreatePayload,
    FieldReorderPayload,
    FieldUpdatePayload,
    UsedOptions,
} from '../interfaces/block';

/**
 * Nav: **Advanced config** — the global block catalogue. See docs/CRM_SECTION_CONTRACT.md §6.
 *
 * Registered as a nested namespaced child of the `crm` module, so actions address as
 * `crm/advancedConfig/<action>` and state as `state.crm.advancedConfig` (see store/index.ts).
 *
 * **Shared between the page's two tracks.** Track A owns the blocks (rail, name, description, colour,
 * order); Track C owns their embedded fields. Because a field lives *inside* its block document, both
 * tracks write the same store entity — so the write path is defined here, up front, rather than
 * invented twice:
 *
 * - `UPSERT_BLOCK` is the only way either track puts a block into state. Every endpoint on both sides
 *   returns the whole updated block, so both commit through it and neither has to patch a sub-path.
 * - Track A must not commit a block's `fields`, and Track C must not commit its `name`, `description`,
 *   `color` or `order`. Returning the server's block from every call makes that automatic; hand-editing
 *   state is what breaks it.
 * - The rail's `N champs` count is read from `fields.length`, so it follows Track C's writes for free.
 */
export interface AdvancedConfigState {
    blocks: Block[];
    selectedBlockId: string | null;
    loading: boolean;
    error: string | null;
    /** By block id. */
    usedOptions: Record<string, UsedOptions>;
}

const defaultState = (): AdvancedConfigState => ({
    blocks: [],
    selectedBlockId: null,
    loading: false,
    error: null,
    usedOptions: {},
});

type Context = ActionContext<AdvancedConfigState, unknown>;

const writeBlock = (
    commit: Context['commit'],
    request: () => Promise<Block>,
): Promise<Block | null> => commitEntity(commit, request, 'crm.advancedConfig.errors.saveFailed');

export const advancedConfigModule: Module<AdvancedConfigState, unknown> = {
    namespaced: true,
    state: defaultState,
    mutations: {
        SET_USED_OPTIONS(
            state: AdvancedConfigState,
            { blockId, usedOptions }: { blockId: string; usedOptions: UsedOptions },
        ): void {
            state.usedOptions = { ...state.usedOptions, [blockId]: usedOptions };
        },
        SET_BLOCKS(state: AdvancedConfigState, blocks: Block[]): void {
            state.blocks = blocks;
        },
        /** Replaces the block in place, or appends it when it is new. Both tracks commit through this. */
        UPSERT_BLOCK(state: AdvancedConfigState, block: Block): void {
            const index = state.blocks.findIndex((candidate) => candidate.id === block.id);
            state.blocks = index === -1
                ? [...state.blocks, block]
                : state.blocks.map((candidate) => (candidate.id === block.id ? block : candidate));
        },
        REMOVE_BLOCK(state: AdvancedConfigState, blockId: string): void {
            state.blocks = state.blocks.filter((block) => block.id !== blockId);
            if (state.selectedBlockId === blockId) {
                state.selectedBlockId = null;
            }
        },
        SELECT_BLOCK(state: AdvancedConfigState, blockId: string | null): void {
            state.selectedBlockId = blockId;
        },
        SET_LOADING(state: AdvancedConfigState, value: boolean): void {
            state.loading = value;
        },
        SET_ERROR(state: AdvancedConfigState, message: string | null): void {
            state.error = message;
        },
    },
    actions: {
        /**
         * The catalogue, for Advanced config's rail **and** Configuration CRM's block palette — the one
         * call two pages share, so it lives here and Configuration CRM's Track E dispatches it rather
         * than fetching blocks itself (§6).
         */
        async fetchBlocks({ commit }: Context): Promise<void> {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            try {
                commit('SET_BLOCKS', await getCrmRequests().blocks.list());
            } catch {
                commit('SET_ERROR', 'crm.advancedConfig.errors.loadFailed');
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async fetchUsedOptions({ commit }: Context, blockId: string): Promise<void> {
            try {
                const { usedOptions } = await getCrmRequests().blocks.usedOptions(blockId);
                commit('SET_USED_OPTIONS', { blockId, usedOptions });
            } catch {
                // The server still refuses removing a used value; only the greyed-out hint is lost.
            }
        },

        async createBlock({ commit }: Context): Promise<boolean> {
            const block = await writeBlock(commit, () => getCrmRequests().blocks.create());
            if (!block) {
                return false;
            }
            commit('UPSERT_BLOCK', block);
            commit('SELECT_BLOCK', block.id);
            return true;
        },

        async updateBlock(
            { commit }: Context,
            { blockId, payload }: { blockId: string; payload: BlockWritePayload },
        ): Promise<boolean> {
            const block = await writeBlock(commit, () => getCrmRequests().blocks.update(blockId, payload));
            if (!block) {
                return false;
            }
            commit('UPSERT_BLOCK', block);
            return true;
        },

        async deleteBlock({ commit }: Context, blockId: string): Promise<boolean> {
            const block = await writeBlock(commit, () => getCrmRequests().blocks.remove(blockId));
            if (!block) {
                return false;
            }
            commit('REMOVE_BLOCK', blockId);
            return true;
        },

        async addBlockField(
            { commit }: Context,
            { blockId, payload }: { blockId: string; payload: FieldCreatePayload },
        ): Promise<boolean> {
            const block = await writeBlock(commit, () => getCrmRequests().blocks.createField(blockId, payload));
            if (!block) {
                return false;
            }
            commit('UPSERT_BLOCK', block);
            return true;
        },

        async updateBlockField(
            { commit, dispatch }: Context,
            { blockId, fieldId, payload }: { blockId: string; fieldId: string; payload: FieldUpdatePayload },
        ): Promise<boolean> {
            const block = await writeBlock(commit, () =>
                getCrmRequests().blocks.updateField(blockId, fieldId, payload),
            );
            if (!block) {
                // A refusal may mean an opportunity chose the value since the page loaded.
                dispatch('fetchUsedOptions', blockId);
                return false;
            }
            commit('UPSERT_BLOCK', block);
            return true;
        },

        async deleteBlockField(
            { commit }: Context,
            { blockId, fieldId }: { blockId: string; fieldId: string },
        ): Promise<boolean> {
            const block = await writeBlock(commit, () => getCrmRequests().blocks.deleteField(blockId, fieldId));
            if (!block) {
                return false;
            }
            commit('UPSERT_BLOCK', block);
            return true;
        },

        async reorderBlockFields(
            { commit }: Context,
            { blockId, payload }: { blockId: string; payload: FieldReorderPayload },
        ): Promise<boolean> {
            const block = await writeBlock(commit, () => getCrmRequests().blocks.reorderFields(blockId, payload));
            if (!block) {
                return false;
            }
            commit('UPSERT_BLOCK', block);
            return true;
        },
    },
    getters: {
        blocks: (state: AdvancedConfigState): Block[] => state.blocks,
        selectedBlock: (state: AdvancedConfigState): Block | null =>
            state.blocks.find((block) => block.id === state.selectedBlockId) ?? null,
        loading: (state: AdvancedConfigState): boolean => state.loading,
        error: (state: AdvancedConfigState): string | null => state.error,
        usedOptions: (state: AdvancedConfigState) => (blockId: string): UsedOptions =>
            state.usedOptions[blockId] ?? {},
    },
};
